import React from 'react';
import { MdIosShare } from 'react-icons/md';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n';

import Button, {
  ButtonType
} from '../../../../legacy/common/components/button/Button';
import JoinButton from '../JoinButton';
import AuthenticationFlow from 'legacy/auth/components/authentication-flow/AuthenticationFlow';
import useAuthenticationStatus from 'legacy/common/hooks/useAuthenticationStatus';
import { OutgoingMembershipDto } from 'api/dtos/memberships/products';

interface Props {
  toggleShareModal: () => void;
  isUserAdmin: boolean;
  editLink?: string;
  dashboardLink?: string;
  manageText: string;
  isUserPending: boolean;
  isUserMember: boolean;
  join: () => void;
  hasOngoingMembership?: boolean;
  upcomingMembership?: OutgoingMembershipDto;
  isJoinBlocked?: boolean;
  blockedMessage?: string;
}

const MainActions = ({
  toggleShareModal,
  editLink,
  dashboardLink,
  isUserAdmin,
  manageText,
  isUserPending,
  isUserMember,
  join,
  hasOngoingMembership,
  upcomingMembership,
  isJoinBlocked = false,
  blockedMessage
}: Props) => {
  const { t } = useTranslation(['organizations', 'associations']);
  const router = useRouter();
  const { isAuthenticated } = useAuthenticationStatus();
  const isLoggedIn = isAuthenticated();
  const isAssociation = router.pathname.includes('associations');
  const organizationId = router?.query?.organizationId as string;

  const goToEdit = () => {
    router.push(editLink);
  };

  const goToDashboard = () => {
    router.push(dashboardLink);
  };

  const goToMembershipsCheckout = () => {
    router.push(`/organizations/${organizationId}/memberships/checkout`);
  };

  return (
    <div className="flex w-full gap-3 pt-1 pb-1">
      <div className="">
        {isLoggedIn ? (
          <>
            {isUserMember &&
              dashboardLink &&
              (isAssociation || (!isAssociation && hasOngoingMembership)) && (
                <Button
                  className="!bg-[#3853C4] !text-white !border-none !w-[223px] !h-[48px] !rounded-full tracking-[-0.12px]"
                  type={ButtonType.Primary}
                  newRadius
                  fillContainer
                  onClick={goToDashboard}
                >
                  {t('organizations:publicView.goToDashboard')}
                </Button>
              )}
            {isUserMember && !isAssociation && !hasOngoingMembership && (
              <Button
                className="!bg-[#3853C4] !text-white !border-none !w-[223px] !h-[48px] !rounded-full tracking-[-0.12px]"
                type={ButtonType.Primary}
                newRadius
                fillContainer
                onClick={goToMembershipsCheckout}
              >
                {upcomingMembership ? (
                  <>
                    {t('organizations:memberships.gate.viewMembership')}
                    <br />
                    {t(
                      'organizations:memberships.membershipsTiers.accessFrom',
                      {
                        startDate: new Date(
                          upcomingMembership?.validFrom
                        ).toLocaleDateString(i18n.language, {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit'
                        })
                      }
                    )}
                  </>
                ) : (
                  t('organizations:memberships.gate.selectMembership')
                )}
              </Button>
            )}
            {isUserMember && !dashboardLink && (
              <JoinButton
                isUserPending={isUserPending}
                isUserMember={isUserMember}
                isUserAdmin={isUserAdmin}
                fillContainer
                join={join}
                isAssociation={isAssociation}
                isJoinBlocked={isJoinBlocked}
                blockedMessage={blockedMessage}
              />
            )}
            {isUserAdmin && editLink && !dashboardLink && (
              <Button
                className="!bg-[#3853C4] !text-white !border-none !w-[223px] !h-[48px] !rounded-full tracking-[-0.12px]"
                type={ButtonType.Primary}
                newRadius
                fillContainer
                onClick={goToEdit}
              >
                {manageText}
              </Button>
            )}
            {((!isUserMember && !isUserAdmin) ||
              (!dashboardLink && !editLink)) && (
              <JoinButton
                isUserPending={isUserPending}
                isUserMember={isUserMember}
                isUserAdmin={isUserAdmin}
                fillContainer
                isAssociation={isAssociation}
                join={join}
                isJoinBlocked={isJoinBlocked}
                blockedMessage={blockedMessage}
              />
            )}
          </>
        ) : (
          <AuthenticationFlow onCompleteAuthentication={join}>
            {({ startAuthenticationFlow }) => (
              <Button
                className="!bg-[#3853C4] !text-white !border-none !w-[223px] !h-[48px] !rounded-full tracking-[-0.12px]"
                onClick={() => startAuthenticationFlow()}
                testId="button-continue"
              >
                {isAssociation
                  ? t('associations:join')
                  : t('organizations:actions.join')}
              </Button>
            )}
          </AuthenticationFlow>
        )}
      </div>
      <div>
        <Button
          onClick={toggleShareModal}
          icon={<MdIosShare size={20} />}
          className="!text-[#3853C4] !bg-[#EFF2FF]  !h-[48px] !w-[48px] !rounded-full !border-[1px] !border-[#d3daf7]"
          transparentBg
          rounded
        />
      </div>
    </div>
  );
};

export default MainActions;
